<template>
  <div class="page-wrap p-3">
    <div class="page-header p-3 mb-3 mt-3">
      <div class="d-flex justify-content-between align-items-center w-100 mb-3">
        <h3>{{ pageTitle }}</h3>
      </div>

      <div class="filters">
        <div>
          <div class="row col-md-9">
            <div class="el-input-group">
              <label for="filterYear" class="form-label col-md-3">
                Год
              </label>
              <div class="col-md-9">
                <select v-model="page.filter.academicYear" id="filterYear"
                        @change="reloadList()"
                        class="form-select">
                  <option v-for="(year, index) in filterYears" :key="index" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="row col-md-9" v-if="isAdmin() || isManagerFC() || isAdminReadOnly()">
            <div class="el-input-group">
              <label for="university" class="form-label col-md-3">
                ВУЗ
              </label>
              <div class="col-md-9">
                <multiselect
                    v-model="page.filter.university"
                    :options="universities"
                    :searchable="true"
                    :closeOnSelect="true"
                    label="nameRu"
                    track-by="universityId"
                    :allow-empty="true"
                    placeholder="Выберите ВУЗ"
                    :multiple="false"
                    :clear-on-select="true"
                    @select="val => {
                      page.filter.universityId = val?.universityId || null;
                      reloadList();
                    }"
                    @remove="val => {
                      page.filter.universityId = null;
                      reloadList();
                    }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-content mt-3 mb-3">
      <div class="page-content-info d-flex align-items-center justify-content-end">
        <b-button v-if="isAdmin() || isManagerFC() || isUniversityManager()" class="btn btn-success" @click="openCreateDialog">
          Добавить
        </b-button>
      </div>

      <div class="page-content-info d-flex align-items-center justify-content-between">
        <div>Найдено: <span>{{ totalCount }}</span></div>
      </div>

      <b-table striped hover
               :items="items"
               :fields="fields"
               responsive="sm"
               no-local-sorting
               show-sort-labels>
        <template #head(universityName)="data">
          <div class="sortable-container none"
               @click="onSortChanged('universityName')"
               :class="{'ascending': page.sort.universityName === 'ASC', 'descending': page.sort.universityName === 'DESC'}">
            ВУЗ
          </div>
        </template>
        <template #head(academicYear)="data">
          <div class="sortable-container none"
               @click="onSortChanged('academicYear')"
               :class="{'ascending': page.sort.academicYear === 'ASC', 'descending': page.sort.academicYear === 'DESC'}">
            Год
          </div>
        </template>
        <template #head(createdAt)="data">
          <div class="sortable-container none"
               @click="onSortChanged('createdAt')"
               :class="{'ascending': page.sort.createdAt === 'ASC', 'descending': page.sort.createdAt === 'DESC'}">
            Дата создания
          </div>
        </template>
        <template #head(actions)="data"></template>

        <template #cell(universityName)="data">
          <a href="#" @click.prevent="handleEdit(data.item)">
            {{ data.item.universityName }}
          </a>
        </template>

        <template #cell(academicYear)="data">{{ data.item.academicYear }} </template>

        <template #cell(staffCount)="data">{{ getSumValue(data.item, 'staffCount') }}</template>
        <template #cell(factCount)="data">{{ getSumValue(data.item, 'factCount') }}</template>
        <template #cell(income)="data">{{ getSumValue(data.item, 'income') }}</template>
        <template #cell(calculatedSum)="data">{{ getSumValue(data.item, 'calculatedSum') }}</template>
        <template #cell(actualSum)="data">{{ getSumValue(data.item, 'actualSum') }}</template>
        
        <template #cell(createdAt)="data">{{ formatDate(data.item.createdAt) }} </template>

        <template #cell(actions)="data">
          <div class="action-cell">
            <el-button type="success" title="Скачать Excel"
                       @click="onClickGenerateAndXls(data.item)">
              <font-awesome-icon :icon="['fas', 'file-excel']"/>
            </el-button>
            <el-button type="danger" title="Удалить"
                       @click="handleDelete(data.item)"
                       v-if="isAdmin() || isManagerFC() || (isUniversityManager() && data.item.status !== 'APPROVED')">
              <font-awesome-icon :icon="['fas', 'trash']"/>
            </el-button>
          </div>
        </template>
      </b-table>
    </div>
    <div class="page-footer">
      <b-pagination
          v-model="page.partNumber"
          :total-rows="totalCount"
          :per-page="page.countInPage"
          v-on:click="reloadList"
          first-text="<<"
          last-text=">>"
          class="mt-3"
      ></b-pagination>
    </div>

    <b-modal id="create-modal" title="Создать запись" v-model="dialogVisible" @ok="handleCreate" ok-title="Создать" cancel-title="Отмена" :ok-disabled="creating">
      <b-form-group label="Год" label-for="year-select">
        <b-form-select v-model="selectedYear" :options="availableYears" id="year-select"></b-form-select>
      </b-form-group>
    </b-modal>

    <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {showErrorNotify, showSuccessNotify} from "@/utils/notificationUtils";
import universityService from "@/services/universityService";
import delegationService from "@/services/delegationService";
import ueExpensesService from "@/services/ueExpensesService";
import Multiselect from "vue-multiselect";
import dayjs from "dayjs";
import { saveAs } from "@/assets/FileSaver.min";

export default {
  components: {
    Multiselect,
    VueLoading
  },
  props: {
    expenseType: {
      type: String,
      required: true
    },
    pageTitle: {
      type: String,
      required: true
    },
    routePrefix: {
      type: String,
      required: true
    }
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      isLoading: false,
      dialogVisible: false,
      creating: false,
      selectedYear: currentYear,
      availableYears: Array.from({length: 8}, (v, i) => 2019 + i),
      page: {
        filter: {
          academicYear: currentYear,
          universityId: null
        },
        sort: {
          academicYear: 'DESC',
          universityName: null,
          createdAt: null
        },
        partNumber: 1,
        countInPage: 10,
      },
      totalCount: 0,
      items: [],
      universities: [],
      filterYears: Array.from({length: 8}, (v, i) => 2019 + i),
    };
  },
  computed: {
    fields() {
      const isOpvr = this.expenseType === 'OPVR';
      const isSocialTax = this.expenseType === 'SOCIAL_TAX';
      const isSocialDed = this.expenseType === 'SOCIAL_DEDUCTION';
      const isOsms = this.expenseType === 'OSMS';
      
      let incomeLabel = 'Ежемесячный доход / Фонд оплаты';
      if (isSocialTax || isSocialDed) incomeLabel = 'Налогооблагаемый фонд оплаты труда';
      else if (isOsms) incomeLabel = 'Объект исчисления отчислений';

      let calcLabel = 'Расчетная сумма';
      if (isOpvr) calcLabel = 'Расчетная сумма взноса';
      else if (isSocialTax) calcLabel = 'Расчетная сумма налога';
      else if (isSocialDed) calcLabel = 'Расчетная сумма социальных отчислений';
      else if (isOsms) calcLabel = 'Расчетная сумма отчислений на обязательное социальное медицинское страхование';

      let actualLabel = 'Фактическая сумма';
      if (isOpvr) actualLabel = 'Фактическая сумма взноса';
      else if (isSocialTax) actualLabel = 'Фактическая сумма налога';
      else if (isSocialDed) actualLabel = 'Фактическая сумма социальных отчислений';
      else if (isOsms) actualLabel = 'Фактическая сумма отчислений на обязательное социальное медицинское страхование';

      return [
        {key: 'universityName', label: 'ВУЗ', sortable: true},
        {key: 'academicYear', label: 'Год', sortable: true},
        {key: 'staffCount', label: 'Штатное кол-во работников (единиц)', sortable: false},
        {key: 'factCount', label: 'Факт-е кол-во работников (единиц)', sortable: false},
        {key: 'income', label: incomeLabel, sortable: false},
        {key: 'calculatedSum', label: calcLabel, sortable: false},
        {key: 'actualSum', label: actualLabel, sortable: false},
        {key: 'createdAt', label: 'Дата создания', sortable: true},
        {key: 'actions', sortable: false}
      ];
    }
  },
  watch: {
    expenseType() {
      this.init();
    }
  },
  methods: {
    init() {
      this.reloadList();
    },
    async reloadList() {
      this.isLoading = true;
      try {
        const response = await ueExpensesService.getList(this.page, this.expenseType);
        this.items = response.list;
        this.totalCount = response.count;
      } catch (e) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        this.isLoading = false;
      }
    },
    onSortChanged(column) {
      this.page.sort = {
        ...this.page.sort,
        [column]: this.page.sort[column] === 'ASC' ? 'DESC' : 'ASC'
      };
      this.reloadList();
    },
    initUniversities() {
      if (this.isAdmin() || this.isManagerFC() || this.isAdminReadOnly()) {
        const params = {
          partNumber: 1,
          countInPage: 0,
          filter: {}
        };
        universityService.getAll(params)
            .then(response => {
              this.universities = response.list.sort((a, b) => a.nameRu.localeCompare(b.nameRu));
            });
      }
    },
    async onClickGenerateAndXls(item) {
      this.isLoading = true;
      try {
        const response = await ueExpensesService.exportExcel(item.uuid);
        const blob = new Blob([response.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
        saveAs(blob, `Расходы_${this.expenseType}_${item.universityId}.xlsx`);
      } catch (error) {
        showErrorNotify('Ошибка при скачивании файла');
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.dialogVisible = true;
    },
    async handleCreate(bvModalEvent) {
      bvModalEvent.preventDefault();
      
      this.creating = true;
      try {
        const response = await ueExpensesService.create({ academicYear: this.selectedYear }, this.expenseType);
        showSuccessNotify('Запись успешно создана');
        this.dialogVisible = false;
        this.reloadList();
      } catch (error) {
        if (error.response && error.response.status === 409) {
          showErrorNotify('Запись для выбранного года уже существует');
        } else {
          showErrorNotify('Ошибка при создании записи');
        }
      } finally {
        this.creating = false;
      }
    },
    handleEdit(row) {
      this.$router.push(`/${this.routePrefix}/${row.uuid}`);
    },
    async handleDelete(item) {
      if (!confirm('Вы уверены, что хотите удалить эту запись?')) return;
      this.isLoading = true;
      try {
        await ueExpensesService.deleteById(item.uuid);
        showSuccessNotify('Запись успешно удалена');
        this.reloadList();
      } catch (error) {
        showErrorNotify('Ошибка при удалении записи');
      } finally {
        this.isLoading = false;
      }
    },
    getSumValue(item, field) {
      if (!item.details || !Array.isArray(item.details)) return 0;
      // The first row is ВСЕГО: (rootDto)
      const rootDetail = item.details.find(d => d.title === 'ВСЕГО:');
      if (rootDetail && rootDetail[field]) {
          return Number(rootDetail[field]).toLocaleString('ru-RU');
      }
      
      // Calculate staffCount/factCount if not on root (our backend does not put staffCount on root, only income/calc/actual)
      if (field === 'staffCount' || field === 'factCount') {
          return item.details
            .filter(d => d.group && d.title !== 'ВСЕГО:')
            .reduce((sum, d) => sum + Number(d[field] || 0), 0)
            .toLocaleString('ru-RU');
      }
      return 0;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return dayjs(dateStr).format('DD.MM.YYYY HH:mm');
    },
    isAdmin() {
      return delegationService.isAdmin();
    },
    isAdminReadOnly() {
      return delegationService.isAdminReadOnly();
    },
    isManagerFC() {
      return delegationService.isManagerFC();
    },
    isUniversityManager() {
      return delegationService.isUniversityManager();
    },
  },
  mounted() {
    this.initUniversities();
    this.init();
  }
};
</script>

<style scoped>
.row { padding-top: 5px; }
.ascending { background-image: url("../assets/asc.svg") !important; }
.descending { background-image: url("../assets/desc.svg") !important; }
.none { background-image: url("../assets/none.svg"); margin-left: 10px !important; }
.sortable-container {
  background-position: right calc(0.75rem / 2) center;
  background-repeat: no-repeat;
  background-size: 0.65em 1em;
  vertical-align: middle !important;
  margin-right: 10px !important;
  padding-right: 15px !important;
  cursor: pointer;
}
th div { width: 100% !important; display: grid !important; }
.p-3 { padding: 0 !important; }
.action-cell { display: flex; justify-content: flex-end; }
.btn-icon { font-size: 30px; cursor: pointer; transition: color 0.3s; margin-right: 2px; }
.btn-excel { color: green; }
.btn-excel:hover { color: #85ce61; }
</style>
